/**
 * Step 6 · Composite Creation.
 * Auto-computes length-weighted composite preview for 1 m; user can
 * pick a different length via a small slider.
 *
 * Feb 2026 (Sprint P37z): mounts the new CompositeBuilderPanel below
 * so the CP can trigger a persistent composite run + review history
 * without dropping to curl.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { compositePreviewFull } from "@/lib/api";
import { WizardCard, StatusRow, KV, LoadingBanner } from "@/pages/wizard/_shared";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import CompositeBuilderPanel from "@/pages/wizard/CompositeBuilderPanel";


export default function CompositeStep({ projectId }) {
  const [length, setLength] = useState(1.0);
  const q = useQuery({
    queryKey: ["wizard-composite", projectId, length],
    queryFn: () => compositePreviewFull(projectId, { composite_length: length }),
    enabled: !!projectId,
  });

  if (q.isLoading) return <LoadingBanner label={`Length-weighted composite @ ${length} m…`} />;
  const d = q.data || {};
  const raw = d?.raw || {};
  const comp = d?.composited || {};

  return (
    <div className="max-w-4xl space-y-4">
      <StatusRow tone="ok">
        Length-weighted compositing at <b>{length.toFixed(2)} m</b>.
        Reduction: <b>{d?.reduction_pct?.toFixed?.(1) ?? "—"}%</b>
        {" "}({raw.n ?? "—"} → {comp.n ?? "—"} intervals).
      </StatusRow>

      <WizardCard title="Composite length" testId="w-composite-slider">
        <Label className="text-[11px] font-semibold text-slate-600">
          Composite length (m) — {length.toFixed(2)}
        </Label>
        <Slider min={0.5} max={5.0} step={0.5}
                 value={[length]}
                 onValueChange={([v]) => setLength(v)}
                 data-testid="w-composite-length" />
      </WizardCard>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <WizardCard title="Raw sample stats" testId="w-composite-raw">
          <KV k="N"      v={raw.n?.toLocaleString?.() ?? "—"} />
          <KV k="Mean"   v={fmt(raw.mean, 3)} />
          <KV k="Median" v={fmt(raw.median, 3)} />
          <KV k="Std"    v={fmt(raw.std, 3)} />
          <KV k="Max"    v={fmt(raw.max, 3)} />
          <KV k="CV"     v={fmt(raw.cv, 3)} />
        </WizardCard>
        <WizardCard title="Composite stats" testId="w-composite-final">
          <KV k="N"      v={comp.n?.toLocaleString?.() ?? "—"} />
          <KV k="Mean"   v={fmt(comp.mean, 3)} />
          <KV k="Median" v={fmt(comp.median, 3)} />
          <KV k="Std"    v={fmt(comp.std, 3)} />
          <KV k="Max"    v={fmt(comp.max, 3)} />
          <KV k="CV"     v={fmt(comp.cv, 3)} />
        </WizardCard>
      </div>

      {/* Sprint P37z · Persistent composite builder — writes to db.composites
                             so downstream stages read the same run. */}
      <CompositeBuilderPanel projectId={projectId} />
    </div>
  );
}


function fmt(v, d = 2) {
  if (v == null || !Number.isFinite(v)) return "—";
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: d });
}
